/* =====================================================================================
   Chorus — internal messaging.

   Two kinds of conversation, and the distinction is the point of the module:

     direct   a conversation between people, which is what Teams chat is for
     object   a conversation anchored to a record: this contract, this centre, this CAPA

   The second is why Chorus exists at all. The discussion about why a discount was given
   belongs on the contract forever, not scrolled away in a channel nobody can find in two
   years. Teams keeps voice, video and anything involving people outside the company.

   Everything written here is a business record: audited, retained, and disclosable in a
   subject access request. That is stated in the interface rather than buried in a policy.
   ===================================================================================== */

import { BACKEND } from './db';
import { TENANT } from '@tenant';

export interface Channel {
  id: string;
  kind: 'direct' | 'object' | 'team' | 'announcement';
  name: string | null;
  other_names: string | null;
  last_message_at: string | null;
  last_body: string | null;
  unread: number;
}

export interface Message {
  id: string;
  channel_id: string;
  author_id: string;
  author_name?: string;
  body: string;
  created_at: string;
  edited_at: string | null;
  is_system: boolean;
}

export interface Colleague {
  id: string;
  full_name: string | null;
  email: string | null;
  job_title: string | null;
}

const live = () => BACKEND === 'supabase';

export async function fetchChannels(): Promise<Channel[]> {
  if (!live()) return (TENANT.seed.channel ?? []) as never;
  const { supabase } = await import('./supabase');
  const { data, error } = await supabase()
    .schema('chorus').from('v_my_channels').select('*')
    .order('last_message_at', { ascending: false, nullsFirst: false });
  if (error) throw new Error(error.message);
  return data ?? [];
}

export async function fetchMessages(channelId: string): Promise<Message[]> {
  if (!live()) return [];
  const { supabase } = await import('./supabase');
  const { data, error } = await supabase()
    .schema('chorus').from('messages')
    .select('*')
    .eq('channel_id', channelId)
    .is('deleted_at', null)
    .order('created_at', { ascending: true })
    .limit(200);
  if (error) throw new Error(error.message);
  const name = await (await import('./supabase')).nameOf();
  return (data ?? []).map((m: any) => ({ ...m, author_name: name(m.author_id) }));
}

/** Everyone you could start a conversation with. RLS already scopes this to your org. */
export async function fetchColleagues(): Promise<Colleague[]> {
  if (!live()) return [];
  const { supabase } = await import('./supabase');
  const { data, error } = await supabase()
    .schema('app').from('members')
    .select('id, full_name, email, job_title')
    .eq('status', 'active')
    .order('full_name');
  if (error) throw new Error(error.message);
  return data ?? [];
}

/**
 * Find or create the conversation with someone. Calling this twice returns the SAME
 * conversation: without that, messaging a colleague a second time splits the history in
 * two and neither half is the whole story.
 */
export async function startDirect(memberId: string): Promise<string> {
  const { supabase } = await import('./supabase');
  const { data, error } = await supabase().schema('chorus').rpc('start_direct', {
    p_other_member: memberId,
  });
  if (error) throw new Error(error.message);
  return data as string;
}

export async function postMessage(channelId: string, body: string): Promise<string> {
  const { supabase } = await import('./supabase');
  const { data, error } = await supabase().schema('chorus').rpc('post_message', {
    p_channel_id: channelId, p_body: body,
  });
  if (error) {
    if (error.code === '42501' || /not a member/i.test(error.message)) {
      throw new Error('You are not a member of this conversation.');
    }
    throw new Error(error.message);
  }
  return data as string;
}

/** Marks the conversation read up to now, which is what clears the unread count. */
export async function markRead(channelId: string) {
  if (!live()) return;
  const { supabase, currentMember } = await import('./supabase');
  const m = await currentMember();
  if (!m?.id) return;
  await supabase().schema('chorus').from('channel_members')
    .update({ last_read_at: new Date().toISOString() })
    .eq('channel_id', channelId).eq('member_id', m.id);
}

export async function fetchNotifications() {
  if (!live()) return [];
  const { supabase } = await import('./supabase');
  const { data } = await supabase()
    .schema('chorus').from('notifications')
    .select('*').is('read_at', null)
    .order('created_at', { ascending: false }).limit(30);
  return data ?? [];
}
